<script setup>
import BaseDialog from "@/base-components/Dialog/BaseDialog.vue";
import Button from "@/base-components/Button";
import {uri} from "@/constants/config.js";
import {onMounted, ref} from "vue";
import {useFetch} from "@/composables/useFetch.js";
import Lucide from "@/base-components/Lucide";
import NetworkDialog from "@/pages/manager/cloud/list/components/NetworkDialog.vue";

const props = defineProps({
  region: {
    type: Object,
  },
  show: {
    type: Boolean,
    default: false
  }
})
const emits = defineEmits(['close'])
const isLoading = ref(true);
const isEditing = ref(false)
const currentNetwork = ref(null)

function close() {
  emits('close')
}

const networks = ref([])

function editNetwork(osNetwork) {
  currentNetwork.value = osNetwork
  isEditing.value = true;
}

async function switchNetworkState(osNetwork) {
  if (!osNetwork.assigned) {
    editNetwork(osNetwork)
    return;
  }

  isLoading.value = true
  const {data} = await useFetch(uri.admin.cloud.updateNetworkUri(props.region.assigned.id, osNetwork.assigned.id), {
    method: 'PATCH',
    body: {isActive: !osNetwork.assigned.isActive}
  })
  osNetwork.assigned = data
  isLoading.value = false
}

async function switchSubnetState(osNetwork, osSubnet) {
  console.log({osNetwork, osSubnet});
  const body = {isActive: true, externalId: osSubnet.subnetId, gateway: ""}
  let method = "POST"
  let url = uri.admin.cloud.subnetUri(props.region.assigned.id, osNetwork.assigned.id)
  if (osSubnet.assigned) {
    body.isActive = !osSubnet.assigned
    method = "PATCH"
    url = uri.admin.cloud.updateSubnetUri(props.region.assigned.id, osNetwork.assigned.id, osSubnet.assigned.id)
  }

  isLoading.value = true
  const {data} = await useFetch(url, {body, method})
  osSubnet.assigned = data
  isLoading.value = false
}

onMounted(async () => {
  const {data} = await useFetch(uri.admin.cloud.networkUri(props.region.assigned.id))
  networks.value = data
  isLoading.value = false
})

</script>

<template>
  <BaseDialog
      :open="show"
      :has-footer="false"
      @close="close"
  >
    <NetworkDialog :show="isEditing" v-model="currentNetwork" :region-id="region.assigned.id" v-if="isEditing"
                   @close="isEditing = false"/>

    <free-style-shimmer width="100%" height="500px" class="w-full" v-if="isLoading"/>

    <div v-if="!isLoading" class="grid grid-cols-3 gap-4 p-5 h-[75vh]" v-for="network in networks">
      <div
          v-if="!isLoading"
          class="rounded-lg p-3 space-y-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow relative box "
          :class="!network.assigned ? 'bg-gray-100' : ''"
      >
        <Lucide :color="!network.assigned ? 'red':'green'" icon="Network" class="absolute inline -mt-5 -mr-3"/>

        <!-- Header Section -->
        <div class="flex items-center justify-between border-b pb-2 mb-4">
          <div class="w-full gap-2">
            <h3 class="text-xl font-medium text-gray-800">
              <Lucide v-if="network.assigned" icon="Edit" class="h-5 cursor-pointer inline"
                      @click="editNetwork(network)"/>
              {{ network.assigned ? network.assigned.displayName : $t('title.notAssigned') }}
            </h3>
          </div>
          <div class="w-36 -mt-3">
                <span class="text-sm text-gray-500">
                  {{ network.assigned ? network.assigned.name : 'N/A' }}
                </span>
            <Button
                :variant="network.assigned ? (network.assigned.isActive ? 'primary' : 'warning') : 'danger'"
                size="sm"
                class="w-16"
                @click="switchNetworkState(network)"
            >
              {{
                network.assigned ? (network.assigned.isActive ? $t("title.active") : $t("title.inactive")) : $t("title.assign")
              }}
            </Button>
          </div>
        </div>
        <!-- Middle Data Fields -->
        <div class="gap-4">
          <div class="flex flex-row mb-2">
            <div class="flex w-full content-between justify-between">
              <p class="text-gray-500 text-sm">netId</p>
              <span class="font-medium text-gray-800">{{ network.netId }}</span>
            </div>
          </div>
        </div>
        <div v-if="network.assigned" class="gap-4 border-b space-y-3 flex">
          <div class="box p-2 w-full" v-for="subnet in network.subnets">
            <div class="w-full mt-3 mb-3 pb-1 border-b">
                <span class="text-sm text-gray-500">
                  {{ subnet.assigned ? subnet.assigned.name : 'N/A' }}
                </span>
              <Button
                  :variant="subnet.assigned ? (subnet.assigned.isActive ? 'primary' : 'warning') : 'danger'"
                  size="sm"
                  class="w-16"
                  @click="switchSubnetState(network, subnet)"
              >
                {{
                  subnet.assigned ? (subnet.assigned.isActive ? $t("title.active") : $t("title.inactive")) : $t("title.assign")
                }}
              </Button>
            </div>
            <div class="flex w-full content-between justify-between">
              <p class="text-gray-500 text-sm">subnetId</p>
              <span class="font-medium text-gray-800">{{ subnet.subnetId }}</span>
            </div>
            <div class="flex w-full content-between justify-between">
              <p class="text-gray-500 text-sm">cidr</p>
              <span class="font-medium text-gray-800">{{ subnet.cidr }}</span>
            </div>
            <div class="flex w-full content-between justify-between">
              <p class="text-gray-500 text-sm">availableIps</p>
              <span class="font-medium text-gray-800">{{ subnet.availableIps }}</span>
            </div>

          </div>
        </div>
      </div>
    </div>
  </BaseDialog>
</template>

<style scoped>

</style>